import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuthState } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";
import { getPropertyImageUrl } from "@/config/api";
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  Home, 
  CalendarDays,
  DollarSign,
  Users,
  AlertCircle,
  Info
} from "lucide-react";
import { AvailabilityService } from "@/services/availabilityService";
import { PropertyService } from "@/services/propertyService";
import { AvailabilityPeriod } from "@/integrations/mysql/types";
import { Property } from "@/integrations/mysql/types";
import { getErrorMessage, parseError, ErrorType } from "@/utils/errorMessages";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { LoadingButton } from "@/components/LoadingButton";

const ManageAvailability = () => {
  const { isAuthenticated, userType, userId, userName, userPhoto } = useAuthState();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  
  const [properties, setProperties] = useState<Property[]>([]);
  const [availabilityPeriods, setAvailabilityPeriods] = useState<AvailabilityPeriod[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [selectedPropertyData, setSelectedPropertyData] = useState<Property | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<AvailabilityPeriod | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    propertyId: "",
    startDate: "",
    endDate: "",
    pricePerNight: "",
    availabilityType: "aluguel" as "troca" | "aluguel" | "troca_aluguel",
    notes: ""
  });

  useEffect(() => {
    if (userId) {
      // Capturar parâmetro property da URL
      const propertyParam = searchParams.get('property');
      if (propertyParam) {
        setSelectedProperty(propertyParam);
        loadPropertyDataAndCheckPermission(parseInt(propertyParam));
      } else {
        // Se não há parâmetro, redirecionar para meus imóveis
        window.location.href = '/minha-area/meus-imoveis';
      }
    }
  }, [userId, searchParams]);

  useEffect(() => {
    if (selectedProperty) {
      loadAvailabilityPeriods(parseInt(selectedProperty));
    }
  }, [selectedProperty]);

  const loadPropertyDataAndCheckPermission = async (propertyId: number) => {
    try {
      setLoading(true);
      const userProperties = await PropertyService.getPropertiesByOwner(userId!);
      setProperties(userProperties); // Manter propriedades atualizadas
      
      const property = userProperties.find(p => p.id === propertyId);
      
      if (property) {
        setSelectedPropertyData(property);
        setHasPermission(true);
        loadAvailabilityPeriods(propertyId);
      } else {
        setHasPermission(false);
        toast({
          title: "Acesso negado",
          description: "Você não tem permissão para gerenciar esta propriedade",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = '/minha-area/meus-imoveis';
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao verificar permissão:', error);
      toast({
        title: "Erro ao verificar permissão",
        description: "Não foi possível verificar suas permissões",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      setLoading(true);
      const userProperties = await PropertyService.getPropertiesByOwner(userId!);
      setProperties(userProperties);
    } catch (error) {
      console.error('Erro ao carregar propriedades:', error);
      toast({
        title: "Erro ao carregar propriedades",
        description: "Não foi possível carregar suas propriedades",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAvailabilityPeriods = async (propertyId: number) => {
    try {
      const periods = await AvailabilityService.getAvailabilityPeriods(propertyId);
      setAvailabilityPeriods(periods);
    } catch (error) {
      console.error('Erro ao carregar períodos:', error);
      toast({
        title: "Erro ao carregar períodos",
        description: "Não foi possível carregar os períodos de disponibilidade",
        variant: "destructive",
      });
    }
  };

  // Função para converter string de data (YYYY-MM-DD) para Date sem aplicar timezone
  const parseDateString = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number);
    // Criar data local (sem timezone) para manter a data exata selecionada
    return new Date(year, month - 1, day);
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.propertyId || !formData.startDate || !formData.endDate || !formData.pricePerNight || !formData.availabilityType) {
      const errorMessage = getErrorMessage(ErrorType.VALIDATION_REQUIRED);
      toast({
        title: errorMessage.title,
        description: errorMessage.description,
        variant: errorMessage.variant || "destructive",
      });
      return;
    }

    const startDate = parseDateString(formData.startDate);
    const endDate = parseDateString(formData.endDate);

    if (startDate >= endDate) {
      const errorMessage = getErrorMessage(ErrorType.VALIDATION_DATE_RANGE);
      toast({
        title: errorMessage.title,
        description: errorMessage.description,
        variant: errorMessage.variant || "destructive",
      });
      return;
    }

    const pricePerNight = parseFloat(formData.pricePerNight);
    if (isNaN(pricePerNight) || pricePerNight < 350) {
      const errorMessage = getErrorMessage(ErrorType.VALIDATION_MIN_VALUE);
      toast({
        title: errorMessage.title,
        description: "O valor mínimo aceito é R$ 350,00 por noite",
        variant: errorMessage.variant || "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      if (editingPeriod) {
        // Atualizar período existente
        await AvailabilityService.updateAvailabilityPeriod(editingPeriod.id, {
          start_date: startDate,
          end_date: endDate,
          price_per_night: parseFloat(formData.pricePerNight),
          availability_type: formData.availabilityType,
          notes: formData.notes || undefined
        });
        
        toast({
          title: "Período atualizado",
          description: "O período de disponibilidade foi atualizado com sucesso",
        });
      } else {
        // Criar novo período
        await AvailabilityService.createAvailabilityPeriod({
          property_id: parseInt(formData.propertyId),
          start_date: startDate,
          end_date: endDate,
          price_per_night: parseFloat(formData.pricePerNight),
          availability_type: formData.availabilityType,
          notes: formData.notes || undefined
        });
        
        toast({
          title: "Período criado",
          description: "O período de disponibilidade foi criado com sucesso",
        });
      }

      // Recarregar períodos
      if (selectedProperty) {
        loadAvailabilityPeriods(parseInt(selectedProperty));
      }
      
      // Limpar formulário
      setFormData({
        propertyId: "",
        startDate: "",
        endDate: "",
        pricePerNight: "",
        availabilityType: "aluguel" as "troca" | "aluguel" | "troca_aluguel",
        notes: ""
      });
      setShowAddModal(false);
      setEditingPeriod(null);
    } catch (error) {
      const errorType = parseError(error);
      const errorMessage = getErrorMessage(errorType);
      toast({
        title: errorMessage.title,
        description: errorMessage.description,
        variant: errorMessage.variant || "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (period: AvailabilityPeriod) => {
    setEditingPeriod(period);
    
    // Debug: verificar o que está sendo recebido
    console.log('🔍 Debug handleEdit - Período recebido:', {
      id: period.id,
      start_date: period.start_date,
      end_date: period.end_date,
      start_date_type: typeof period.start_date,
      end_date_type: typeof period.end_date
    });
    
    // Formatar datas corretamente para o input (YYYY-MM-DD)
    const formatDateForInput = (date: Date | string) => {
      console.log('🔍 Debug formatDateForInput - Data recebida:', { date, type: typeof date });
      
      // Se for string, extrair apenas a parte da data (YYYY-MM-DD)
      if (typeof date === 'string') {
        // Se contém 'T' (formato ISO), extrair apenas a parte da data
        if (date.includes('T')) {
          const result = date.split('T')[0];
          console.log('🔍 Debug - String com T, resultado:', result);
          return result;
        }
        // Se já está no formato YYYY-MM-DD, retornar como está
        if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          console.log('🔍 Debug - String já no formato correto:', date);
          return date;
        }
      }
      
      // Se for objeto Date, converter para YYYY-MM-DD usando data local
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const result = `${year}-${month}-${day}`;
      console.log('🔍 Debug - Date convertido (local):', { original: date, result });
      return result;
    };
    
    const formattedStartDate = formatDateForInput(period.start_date);
    const formattedEndDate = formatDateForInput(period.end_date);
    
    console.log('🔍 Debug - Datas formatadas:', {
      startDate: formattedStartDate,
      endDate: formattedEndDate
    });
    
    setFormData({
      propertyId: period.property_id.toString(),
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      pricePerNight: period.price_per_night.toString(),
      availabilityType: period.availability_type || "aluguel",
      notes: period.notes || ""
    });
    setShowAddModal(true);
  };

  const handleDelete = async (periodId: number) => {
    if (!confirm("Tem certeza que deseja excluir este período?")) return;

    try {
      await AvailabilityService.deleteAvailabilityPeriod(periodId);
      toast({
        title: "Período excluído",
        description: "O período de disponibilidade foi excluído com sucesso",
      });
      
      if (selectedProperty) {
        loadAvailabilityPeriods(parseInt(selectedProperty));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Não foi possível excluir o período de disponibilidade";
      toast({
        title: "Erro ao excluir período",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  // Formatar data SEM timezone - usar exatamente como vem do banco (YYYY-MM-DD)
  const formatDate = (date: Date | string) => {
    let dateStr: string;
    
    // Se for string, extrair apenas a parte da data (YYYY-MM-DD)
    if (typeof date === 'string') {
      dateStr = date.substring(0, 10);
    } else {
      // Se for Date, extrair componentes UTC para evitar timezone
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      dateStr = `${year}-${month}-${day}`;
    }
    
    // Parsear como YYYY-MM-DD e formatar
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    
    return dateStr;
  };

  // Calcular noites SEM timezone - usar datas exatas do banco
  const calculateNights = (startDate: Date | string, endDate: Date | string) => {
    try {
      // Extrair apenas a parte da data (YYYY-MM-DD)
      const startStr = typeof startDate === 'string' ? startDate.substring(0, 10) : 
        `${startDate.getUTCFullYear()}-${String(startDate.getUTCMonth() + 1).padStart(2, '0')}-${String(startDate.getUTCDate()).padStart(2, '0')}`;
      const endStr = typeof endDate === 'string' ? endDate.substring(0, 10) : 
        `${endDate.getUTCFullYear()}-${String(endDate.getUTCMonth() + 1).padStart(2, '0')}-${String(endDate.getUTCDate()).padStart(2, '0')}`;
      
      // Parsear diretamente como YYYY-MM-DD usando UTC para evitar problemas de timezone
      const parseDate = (dateStr: string) => {
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(Date.UTC(year, month - 1, day));
        }
        return new Date(dateStr);
      };
      
      const start = parseDate(startStr);
      const end = parseDate(endStr);
      
      // Verificar se as datas são válidas
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        console.error('Datas inválidas:', { startDate, endDate, start, end });
        return 0;
      }
      
      const timeDiff = end.getTime() - start.getTime();
      const nights = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
      
      return nights;
    } catch (error) {
      console.error('Erro ao calcular diárias:', error);
      return 0;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingSpinner size="lg" text="Carregando..." />
    </div>
    );
  }

  // Verificação de permissão
  if (!hasPermission) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-destructive mb-2">Acesso Negado</h1>
          <p className="text-muted-foreground mb-4">
            Você não tem permissão para gerenciar esta propriedade.
          </p>
          <Button onClick={() => window.location.href = '/minha-area/meus-imoveis'}>
            Voltar para Meus Imóveis
          </Button>
    </div>
    </div>
    );
  }

  return (
    <MinhaAreaPageShell>
<div className="flex items-center gap-2 mb-6">
        <CalendarDays className="h-6 w-6" />
        <h1 className="text-3xl font-bold">Gerenciar Disponibilidade</h1>
    </div>

      {/* Card da Propriedade Selecionada */}
      {selectedPropertyData && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Home className="h-5 w-5" />
              Propriedade Selecionada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-semibold mb-2">{selectedPropertyData.title}</h3>
                <p className="text-muted-foreground mb-4">{selectedPropertyData.description}</p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      <strong>{selectedPropertyData.max_guests || 'N/A'}</strong> hóspedes
                    </span>
    </div>
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {selectedPropertyData.property_type || 'N/A'} • {selectedPropertyData.location || 'N/A'}
                    </span>
    </div>
    </div>
    </div>
              
              {/* Foto de capa do imóvel */}
              {selectedPropertyData.cover_image && (
                <div className="flex justify-center">
                  <img 
                    src={getPropertyImageUrl(selectedPropertyData.id, selectedPropertyData.cover_image)}
                    alt={selectedPropertyData.title}
                    className="w-full h-48 object-cover rounded-lg"
                    onError={(e) => {
                      // Fallback para imagem padrão se a capa não existir
                      const target = e.target as HTMLImageElement;
                      target.src = '/placeholder.svg';
                    }}
                  />
    </div>
              )}
    </div>
          </CardContent>
        </Card>
      )}

      {/* Botão Adicionar Período */}
      <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Períodos de Disponibilidade</h2>
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  setEditingPeriod(null);
                  setFormData({
                    propertyId: selectedProperty,
                    startDate: "",
                    endDate: "",
                    pricePerNight: "",
                    availabilityType: "aluguel" as "troca" | "aluguel" | "troca_aluguel",
                    notes: ""
                  });
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Período
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>
                    {editingPeriod ? "Editar Período" : "Adicionar Período"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Data de Início *</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                        required
                      />
    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endDate">Data de Fim *</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                        min={formData.startDate}
                        required
                      />
    </div>
    </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="pricePerNight">Preço por Noite (R$) *</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>O valor mínimo aceito é R$ 350,00</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <Input
                      id="pricePerNight"
                      type="number"
                      step="0.01"
                      min="350"
                      value={formData.pricePerNight}
                      onChange={(e) => setFormData({...formData, pricePerNight: e.target.value})}
                      required
                    />
    </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="availabilityType">Tipo de Disponibilidade *</Label>
                    <Select
                      value={formData.availabilityType}
                      onValueChange={(value: "troca" | "aluguel" | "troca_aluguel") => 
                        setFormData({...formData, availabilityType: value})
                      }
                      required
                    >
                      <SelectTrigger id="availabilityType">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="troca">Troca</SelectItem>
                        <SelectItem value="aluguel">Aluguel</SelectItem>
                        <SelectItem value="troca_aluguel">Troca/Aluguel</SelectItem>
                      </SelectContent>
                    </Select>
    </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Observações</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Ex: Período de alta temporada, promoção especial, etc."
                    />
    </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddModal(false)}
                      disabled={submitting}
                    >
                      Cancelar
                    </Button>
                    <LoadingButton 
                      type="submit" 
                      loading={submitting}
                      loadingText={editingPeriod ? "Atualizando..." : "Criando..."}
                    >
                      {editingPeriod ? "Atualizar" : "Criar"} Período
                    </LoadingButton>
    </div>
                </form>
              </DialogContent>
            </Dialog>
    </div>

          {/* Lista de Períodos */}
          {availabilityPeriods.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhum período cadastrado</h3>
                <p className="text-muted-foreground">
                  Adicione períodos de disponibilidade para que os hóspedes possam fazer reservas.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {availabilityPeriods.map((period) => (
                <Card key={period.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-lg font-semibold mb-1">
                              {formatDate(period.start_date)} - {formatDate(period.end_date)}
                            </h3>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-muted-foreground">
                                {calculateNights(period.start_date, period.end_date)} diária{calculateNights(period.start_date, period.end_date) !== 1 ? 's' : ''}
                              </span>
                              <span className="font-semibold text-green-600">
                                Total: R$ {(calculateNights(period.start_date, period.end_date) * period.price_per_night).toLocaleString('pt-BR')}
                              </span>
    </div>
    </div>
                          <Badge variant={period.is_active ? "default" : "secondary"}>
                            {period.is_active ? "Ativo" : "Inativo"}
                          </Badge>
    </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              <strong>R$ {period.price_per_night.toLocaleString('pt-BR')}</strong>/noite
                            </span>
    </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              <strong>{calculateNights(period.start_date, period.end_date)}</strong> diária{calculateNights(period.start_date, period.end_date) !== 1 ? 's' : ''}
                            </span>
    </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {period.availability_type === 'troca' ? 'Troca' : 
                               period.availability_type === 'aluguel' ? 'Aluguel' : 
                               'Troca/Aluguel'}
                            </Badge>
    </div>
    </div>

                        {period.notes && (
                          <div className="mb-4">
                            <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                              {period.notes}
                            </p>
    </div>
                        )}

                        <div className="text-xs text-muted-foreground">
                          Criado em: {formatDate(period.created_at)}
    </div>
    </div>

                      <div className="flex flex-col gap-2 lg:min-w-[120px]">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(period)}
                          className="w-full gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(period.id)}
                          className="w-full gap-2 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                          Excluir
                        </Button>
    </div>
    </div>
                  </CardContent>
                </Card>
              ))}
    </div>
          )}
    </MinhaAreaPageShell>
  );
};

export default ManageAvailability;
